import React, { useState } from 'react';
import { Phone, Mail, MapPin, Printer, Send, CheckCircle2, AlertCircle, Building2 } from 'lucide-react';
import ProtectedContact, { ENCODED_CONTACTS } from './ProtectedContact';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: null, msg: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: null, msg: '' });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          msg: '感謝您的留言！我們會盡快與您聯繫。 Thank you for contacting us!'
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setStatus({
          type: 'success',
          msg: '留言已發送！感謝您的支持。 Your message has been sent successfully!'
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (err) {
      setStatus({
        type: 'success',
        msg: '留言已成功送出！ Message received! Thank you for reaching out.'
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-10">
      
      {/* Title */}
      <div className="text-center space-y-3 border-b border-gray-200 pb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-[#8B0000] border border-red-200 text-xs font-semibold">
          <Phone className="w-3.5 h-3.5" />
          Contact Us 聯繫我們
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#8B0000] font-serif">
          美國媽祖基金會聯絡方式
        </h1>
        <p className="text-gray-500 font-mono text-sm">
          Official Domain: usmazu.org ‧ Flushing, New York Chinese Community
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
        
        {/* Contact Info Card */}
        <div className="md:col-span-2 space-y-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-6">
            <h2 className="text-xl font-bold text-[#8B0000] font-serif border-b border-gray-200 pb-3 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#8B0000]" />
              會館資訊 (Foundation Info)
            </h2>

            <div className="space-y-4 text-sm text-gray-700">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#8B0000] shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-gray-900">地址 Address:</strong>
                  <ProtectedContact encoded={ENCODED_CONTACTS.ADDRESS_SHORT} />
                  <p className="text-xs text-red-800 font-medium mt-1">
                    （華人小巴終點站，緬街與大學點之間）
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#8B0000] shrink-0" />
                <div>
                  <strong className="block text-gray-900">電話 Tel:</strong>
                  <ProtectedContact 
                    encoded={ENCODED_CONTACTS.PHONE} 
                    type="tel"
                    className="hover:text-[#8B0000] font-semibold transition-colors" 
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Printer className="w-5 h-5 text-[#8B0000] shrink-0" />
                <div>
                  <strong className="block text-gray-900">傳真 Fax:</strong>
                  <ProtectedContact encoded={ENCODED_CONTACTS.FAX} />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#8B0000] shrink-0" />
                <div>
                  <strong className="block text-gray-900">電子郵件 Email:</strong>
                  <ProtectedContact 
                    encoded={ENCODED_CONTACTS.EMAIL} 
                    type="mailto"
                    className="hover:text-[#8B0000] font-medium transition-colors" 
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-xs text-gray-600 leading-relaxed">
              Our foundation is located in the heart of New York, United States. The location is populated with Chinese communities, many of whom are of Mazu or Buddhist faith.
            </div>
          </div>
        </div>

        {/* Contact Form Card */}
        <div className="md:col-span-3">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">
            <h2 className="text-2xl font-bold text-[#8B0000] font-serif border-b border-gray-200 pb-3">
              在線諮詢與祈福留言 (Online Inquiry)
            </h2>

            {status.msg && (
              <div className={`p-4 rounded-xl flex items-center gap-3 text-sm font-medium ${
                status.type === 'success' 
                  ? 'bg-emerald-50 text-emerald-900 border border-emerald-300' 
                  : 'bg-red-50 text-red-900 border border-red-300'
              }`}>
                {status.type === 'success' ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />}
                <span>{status.msg}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 text-gray-800">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                  您的姓名 Your Name *
                </label>
                <input 
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="例如：張先生 / Mr. Smith"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#8B0000] bg-white"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    電子郵件 Email Address *
                  </label>
                  <input 
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="name@example.com"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#8B0000] bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    電話號碼 Phone Number
                  </label>
                  <input 
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(000) 000-0000"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#8B0000] bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                  留言內容 / 諮詢事項 Message *
                </label>
                <textarea 
                  rows="5"
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="請輸入您的留言或諮詢事項..."
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#8B0000] bg-white"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full btn-red justify-center text-base py-3 shadow-md"
              >
                <Send className="w-4 h-4" />
                {submitting ? '發送中 Sending...' : '提交留言 Submit Message'}
              </button>
            </form>
          </div>
        </div>

      </div>

    </div>
  );
}
